import React from 'react';
import { ErrorBoundary } from '@/components/error-boundary';
import { AlertTriangle, RefreshCw, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface FormErrorBoundaryProps {
  children: React.ReactNode;
  formName?: string;
}

export function FormErrorBoundary({ children, formName }: FormErrorBoundaryProps) {
  const handleError = (error: Error) => {
    console.error(`Form error in ${formName}:`, error);
  };

  const formErrorFallback = (
    <div className="flex items-center justify-center min-h-[400px] p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
            <AlertTriangle className="h-6 w-6 text-destructive" />
          </div>
          <CardTitle>{formName ? `${formName} Error` : 'Form Error'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground text-center">
            {formName 
              ? `The ${formName} form encountered an error and cannot be displayed.`
              : 'This form encountered an error and cannot be displayed.'
            }
          </p>
          <div className="flex flex-col gap-2">
            <Button 
              onClick={() => window.location.reload()} 
              variant="outline" 
              className="w-full"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh Page
            </Button>
            <Button 
              onClick={() => window.location.href = 'mailto:support@acehorizon.ai?subject=Contact Form Error'} 
              variant="outline"
              className="w-full"
            >
              <Mail className="mr-2 h-4 w-4" />
              Contact Support
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <ErrorBoundary
      level="component"
      onError={handleError}
      fallback={formErrorFallback}
    >
      {children}
    </ErrorBoundary>
  );
}
